import React, { useMemo, useState } from 'react'
import classNames from 'classnames'
import { Form } from 'react-bootstrap'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { useUpdateAccountMutation } from '../../../../store/accountUserSettings/accountUserSettings'
import { useGetAllUsersQuery } from '../../../../store/user/user'
import { Account, MeetingPrepEmailConfig } from '../../../../models/Account'
import { MultiSelectDropdown, MultiSelectOption } from '../../../../components/MultiSelectDropdown/MultiSelectDropdown'
import { getUserDisplayName } from '../../../../apps/professional-app/fa/common/aifeatures/utils'
import { Button } from '../../../../components/Button/Button'
import { useAutoMeetingPrep } from '../AutoMeetingPrep/hooks'
import { useNavigate } from 'react-router-dom'
import { useZocksAuth } from '../../../../components/ScopedBlock/hooks'
import { EnterpriseManagedBanner } from '../EnterpriseManagedBanner'
import { WhoGetsNotifiedHeader } from './WhoGetsNotifiedHeader'

interface MeetingPrepEmailFormValues {
  enabled: boolean
  includeInfo: 'linkOnly' | 'linkAndSummary'
  includeTitleInSubject: boolean
  notifyMeetingOwner: boolean
  notifyTeamMembers: boolean
  teamMemberIds: string[]
}

const getDefaultFormValues = (config: MeetingPrepEmailConfig): MeetingPrepEmailFormValues => ({
  enabled: config.enabled,
  includeInfo: config.includeInfo,
  includeTitleInSubject: config.includeTitleInSubject ?? true,
  notifyMeetingOwner: config.notifications.meetingOwner,
  notifyTeamMembers: config.notifications.teamMemberIds.length > 0,
  teamMemberIds: config.notifications.teamMemberIds,
})

export interface MeetingPrepEmailNotificationsProps {
  account: Account
}

export const MeetingPrepEmailNotifications = ({ account }: MeetingPrepEmailNotificationsProps) => {
  const navigate = useNavigate()

  const accountId = useAppSelector(selectAccountId)
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)

  const { autoMeetingPrepWorkflow } = useAutoMeetingPrep()

  const [updateAccount, { isLoading: accountUpdateLoading }] = useUpdateAccountMutation()

  const { data: usersData, isFetching: isUsersLoading } = useGetAllUsersQuery({ accountId, identityTypes: ['idp'] })

  const isMeetingPrepEmailEnterpriseManaged = useMemo(() => account?.enterpriseConfig.meetingPrepEmail != null, [account])

  const teamMemberOptions: MultiSelectOption[] = useMemo(() => {
    if (!usersData?.data) {
      return []
    }
    return usersData.data
      .filter((u) => u.id !== currentUserId)
      .sort((u1, u2) => getUserDisplayName(u1).localeCompare(getUserDisplayName(u2)))
      .map((u) => ({ id: u.id, label: getUserDisplayName(u) }))
  }, [usersData, currentUserId])

  const { control, handleSubmit, reset, formState } = useForm<MeetingPrepEmailFormValues>({
    defaultValues: getDefaultFormValues(account.effectiveConfig.meetingPrepEmail),
  })

  const enabled = useWatch({ control, name: 'enabled' })
  const includeInfo = useWatch({ control, name: 'includeInfo' })
  const notifyTeamMembers = useWatch({ control, name: 'notifyTeamMembers' })
  const [isOpened, setIsOpened] = useState<boolean>(false)
  const { hasScope } = useZocksAuth()
  const hasAccountUpdateScope = hasScope('**', 'account:update', ['account:admin'])

  const onSubmit = async (data: MeetingPrepEmailFormValues) => {
    const meetingPrepEmail: MeetingPrepEmailConfig = {
      enabled: data.enabled,
      includeInfo: data.includeInfo,
      includeTitleInSubject: data.includeTitleInSubject,
      notifications: {
        meetingOwner: data.notifyMeetingOwner,
        teamMemberIds: data.notifyTeamMembers ? data.teamMemberIds : [],
      },
    }
    const updatedAccount = await updateAccount({
      accountId,
      payload: {
        config: {
          ...account.config,
          meetingPrepEmail,
        },
      },
    })
    if ('data' in updatedAccount && updatedAccount.data) {
      reset(getDefaultFormValues(updatedAccount.data.data.effectiveConfig.meetingPrepEmail))
    }
  }

  const handleCancel = () => {
    reset(getDefaultFormValues(account.effectiveConfig.meetingPrepEmail))
  }

  return (
    <div className='d-flex flex-column gap-4 border-bottom zocks-pb-2 zocks-border-color-gray-400 border-1'>
      {isMeetingPrepEmailEnterpriseManaged ? (
        <EnterpriseManagedBanner message='Automatic Meeting Prep Ready Email setting is managed at the enterprise level and cannot be modified here.' />
      ) : null}
      <div className='d-flex zocks-gap-0.75 align-items-center justify-content-between'>
        <div className='d-flex flex-column'>
          <h6>Automatic Meeting Prep Ready</h6>
          <div className='paragraph-small zocks-text-gray-700'>Sent when meeting preps are created automatically before scheduled meetings.</div>
        </div>
        <div className='d-flex flex-row gap-2 align-items-center'>
          <Controller
            name='enabled'
            control={control}
            render={({ field }) => (
              <Form.Switch
                id='meeting-prep-email-enabled'
                checked={!!field.value}
                onChange={(e) => {
                  field.onChange(e.target.checked)
                  handleSubmit(onSubmit)()
                }}
                disabled={accountUpdateLoading || !hasAccountUpdateScope || isMeetingPrepEmailEnterpriseManaged}
              />
            )}
          />
          <Button onClick={() => setIsOpened((prev) => !prev)}>
            <i className={isOpened ? 'bi bi-chevron-up' : 'bi bi-chevron-down'}></i>
          </Button>
        </div>
      </div>
      {enabled && isOpened && (
        <>
          {!autoMeetingPrepWorkflow ? (
            <div className='d-flex align-items-center zocks-gap-0.75 px-3 zocks-py-0.75 w-100 zocks-bg-color-warning zocks-border-0.25'>
              <i className='bi bi-exclamation-triangle' />
              <span className='paragraph-small'>
                Sending these notifications requires Automatic Meeting Prep generation. Enable in{' '}
                <span
                  className='paragraph-small zocks-text-gray-900 bold cursor-pointer text-decoration-underline'
                  onClick={() => navigate('../ai-assistant', { replace: true })}
                >
                  AI Assistant
                </span>{' '}
                settings.
              </span>
            </div>
          ) : null}
          <div className='d-flex flex-column zocks-gap-0.75'>
            <div className='paragraph-medium medium'>Email content</div>
            <Controller
              name='includeInfo'
              control={control}
              render={({ field }) => (
                <div className='d-flex flex-column gap-2'>
                  <Form.Check
                    type='radio'
                    id='include-info-link-only-prep'
                    label='Link only'
                    value='linkOnly'
                    checked={field.value === 'linkOnly'}
                    onChange={() => field.onChange('linkOnly')}
                    disabled={accountUpdateLoading || !hasAccountUpdateScope || isMeetingPrepEmailEnterpriseManaged}
                  />
                  <Form.Check
                    type='radio'
                    id='include-info-link-and-summary-prep'
                    label='Link and prep summary'
                    value='linkAndSummary'
                    checked={field.value === 'linkAndSummary'}
                    onChange={() => field.onChange('linkAndSummary')}
                    disabled={accountUpdateLoading || !hasAccountUpdateScope || isMeetingPrepEmailEnterpriseManaged}
                  />
                </div>
              )}
            />
            {includeInfo !== 'linkOnly' ? (
              <div className='d-flex flex-column zocks-gap-0.75'>
                <div className='paragraph-medium medium'>Email subject</div>
                <Controller
                  name='includeTitleInSubject'
                  control={control}
                  render={({ field }) => (
                    <Form.Check
                      type='checkbox'
                      id='include-title-in-subject-prep'
                      label='Include meeting title in subject'
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      disabled={accountUpdateLoading || !hasAccountUpdateScope || isMeetingPrepEmailEnterpriseManaged}
                    />
                  )}
                />
              </div>
            ) : null}
          </div>

          <div className='d-flex flex-column zocks-gap-0.75'>
            <WhoGetsNotifiedHeader />
            <Controller
              name='notifyMeetingOwner'
              control={control}
              render={({ field }) => (
                <Form.Check
                  type='checkbox'
                  id='notify-meeting-owner'
                  label='Meeting owner'
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  disabled={accountUpdateLoading || !hasAccountUpdateScope || isMeetingPrepEmailEnterpriseManaged}
                />
              )}
            />

            <div className='d-flex flex-column gap-2'>
              <Controller
                name='notifyTeamMembers'
                control={control}
                render={({ field }) => (
                  <Form.Check
                    className=''
                    type='checkbox'
                    id='notify-team-members'
                    label='Select team members'
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    disabled={accountUpdateLoading || isUsersLoading || !hasAccountUpdateScope || isMeetingPrepEmailEnterpriseManaged}
                  />
                )}
              />
              {notifyTeamMembers && (
                <div className='ms-4'>
                  <Controller
                    name='teamMemberIds'
                    control={control}
                    render={({ field }) => (
                      <MultiSelectDropdown
                        name={field.name}
                        options={teamMemberOptions}
                        initialSelectedOptions={field.value}
                        onSelectedChanged={field.onChange}
                        placeholder='Select team members…'
                        disabled={accountUpdateLoading || isUsersLoading || !hasAccountUpdateScope || isMeetingPrepEmailEnterpriseManaged}
                      />
                    )}
                  />
                </div>
              )}
            </div>
          </div>

          <div className='d-flex zocks-gap-0.625'>
            <Button
              variant='primary'
              onClick={handleSubmit(onSubmit)}
              loading={accountUpdateLoading}
              disabled={accountUpdateLoading || !formState.isDirty || !hasAccountUpdateScope || isMeetingPrepEmailEnterpriseManaged}
            >
              Save changes
            </Button>
            <Button
              variant='borderless'
              className={classNames({ 'zocks-text-primary': !accountUpdateLoading && formState.isDirty })}
              onClick={handleCancel}
              disabled={accountUpdateLoading || !formState.isDirty || !hasAccountUpdateScope}
            >
              Cancel
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
